// "Acomodar plano": el admin arrastra mesas y áreas de UNA zona sobre un
// borrador; nada se guarda hasta tocar "Guardar plano" (PUT /mesas/plano).
import { useState } from 'react';
import api from '../../services/api';
import { btnPrimario, btnSecundario, campo } from '../../components/ui/estilos';
import { Area, Mesa, zonaDe } from './mesas';
import type { Seleccion } from './PlanoSalon';

type Caja = { pos_x: number; pos_y: number; ancho: number; alto: number };
interface Borrador { mesas: Mesa[]; areasZona: Area[]; areasOtras: Area[] }

const AREA_NUEVA = { pos_x: 40, pos_y: 40, ancho: 200, alto: 80 };

export function useAcomodar(mesas: Mesa[], areas: Area[], zona: string, onGuardado: () => void, say: (m: string) => void) {
  const [b, setB] = useState<Borrador | null>(null);
  const [seleccion, setSeleccion] = useState<Seleccion>(null);
  const [nombreArea, setNombreArea] = useState('');
  const [guardando, setGuardando] = useState(false);

  const deZona = (a: Area) => (a.zona?.trim() || zonaDe({ zona: null })) === zona;

  const iniciar = () => {
    setB({
      mesas: mesas.filter(m => zonaDe(m) === zona).map(m => ({ ...m })),
      areasZona: areas.filter(deZona).map(a => ({ ...a })),
      areasOtras: areas.filter(a => !deZona(a)),
    });
    setSeleccion(null);
  };

  const cambiarMesa = (id: string, c: Partial<Caja & { forma: Mesa['forma'] }>) =>
    setB(x => x && { ...x, mesas: x.mesas.map(m => m.id === id ? { ...m, ...c } : m) });
  const cambiarArea = (idx: number, c: Partial<Caja & { nombre: string }>) =>
    setB(x => x && { ...x, areasZona: x.areasZona.map((a, i) => i === idx ? { ...a, ...c } : a) });

  const agregarArea = () => {
    const nombre = nombreArea.trim();
    if (!nombre) return say('Escribe el nombre del área (ej. Cocina, Entrada).');
    setB(x => x && { ...x, areasZona: [...x.areasZona, { zona, nombre: nombre.slice(0, 40), ...AREA_NUEVA }] });
    setSeleccion({ tipo: 'area', idx: b?.areasZona.length ?? 0 });
    setNombreArea('');
  };

  const quitarArea = (idx: number) => {
    setB(x => x && { ...x, areasZona: x.areasZona.filter((_, i) => i !== idx) });
    setSeleccion(null);
  };

  const guardar = async () => {
    if (!b) return;
    setGuardando(true);
    try {
      await api.put('/mesas/plano', {
        mesas: b.mesas.map(m => ({ id: m.id, pos_x: m.pos_x, pos_y: m.pos_y, ancho: m.ancho, alto: m.alto, forma: m.forma })),
        areas: [...b.areasOtras, ...b.areasZona.map(a => ({ ...a, zona }))],
      });
      setB(null);
      onGuardado();
    } catch (e: any) {
      say(e.response?.data?.error || 'No se pudo guardar el plano.');
    } finally { setGuardando(false); }
  };

  const mesaSel = seleccion?.tipo === 'mesa' ? b?.mesas.find(m => m.id === seleccion.id) : undefined;
  const areaSel = seleccion?.tipo === 'area' ? b?.areasZona[seleccion.idx] : undefined;

  const barra = b && (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '10px 10px 10px 18px', borderRadius: 12, background: 'var(--strong)', color: 'var(--on-strong)' }}>
      <div style={{ flex: '1 1 260px', fontSize: 16, fontWeight: 700 }}>
        Acomodando {zona}: arrastra mesas y áreas; la esquina roja cambia el tamaño. Con las flechas se mueve lo seleccionado.
      </div>
      {mesaSel && (
        <button style={btnSecundario} onClick={() => cambiarMesa(mesaSel.id, { forma: mesaSel.forma === 'redonda' ? 'cuadrada' : 'redonda' })}>
          Mesa {mesaSel.numero}: {mesaSel.forma === 'redonda' ? 'hacer cuadrada' : 'hacer redonda'}
        </button>
      )}
      {areaSel && seleccion?.tipo === 'area' && <>
        <input aria-label="Nombre del área" value={areaSel.nombre} maxLength={40} onChange={e => cambiarArea(seleccion.idx, { nombre: e.target.value })} style={{ ...campo, width: 180, height: 48 }} />
        <button style={btnSecundario} onClick={() => quitarArea(seleccion.idx)}>Quitar área</button>
      </>}
      <input aria-label="Nueva área" placeholder="Nueva área (ej. Cocina)" value={nombreArea} maxLength={40}
        onChange={e => setNombreArea(e.target.value)} onKeyDown={e => e.key === 'Enter' && agregarArea()} style={{ ...campo, width: 200, height: 48 }} />
      <button style={btnSecundario} onClick={agregarArea}>+ Área</button>
      <button style={{ ...btnSecundario, background: 'transparent', color: 'var(--on-strong)', borderColor: 'var(--on-strong)' }} onClick={() => setB(null)}>Cancelar</button>
      <button style={{ ...btnPrimario, height: 48, fontSize: 16, opacity: guardando ? .6 : 1 }} disabled={guardando} onClick={guardar}>
        {guardando ? 'Guardando…' : 'Guardar plano'}
      </button>
    </div>
  );

  return {
    activo: !!b, iniciar, barra, seleccion, seleccionar: setSeleccion,
    mesas: b?.mesas ?? [], areas: b?.areasZona ?? [],
    cambiarMesa, cambiarArea,
  };
}

